import { Platform } from "react-native";

/**
 * A per-tab, throwaway stand-in for the user id a profile screen is showing.
 *
 * ## Why the id cannot be in the URL
 *
 * On web the route is an address bar, a history entry, and a `Referer`. It is
 * also, per `public/_headers`, a CSP-report field: `document-uri` and
 * `referrer` carry the full query string, and a CSP report is sent by the
 * BROWSER directly to Sentry — bypassing every redaction layer in
 * `SENTRY_DATA_POLICY.md §5`, because none of our code ever sees it. So
 * `/guest-profile?id=<another user's id>` shipped somebody else's identifier
 * to a third party on any violation. That is a different class of exposure
 * from a person's own `session_id`: this id belongs to the person being
 * LOOKED AT, who did not choose to be here.
 *
 * The display name was considered and rejected as the replacement. It is not
 * unique and can be empty, so it cannot resolve; and a real name in an address
 * bar reads worse to a human — over a shoulder, in a screenshot, on a shared
 * screen — than a UUID a human cannot interpret at all.
 *
 * ## What this is instead
 *
 * A random token minted at navigation time, mapped to the id in
 * `sessionStorage`, and put in the URL in its place. It means nothing outside
 * the page session that minted it: a CSP report, a log line, a copied link or
 * a colleague's browser resolves it to nothing.
 *
 * `sessionStorage`, deliberately, and NOT the app's `storage` helper — that
 * one is `localStorage` on web, which is shared by every tab and outlives all
 * of them, so it would accumulate the id of everyone the user had ever looked
 * at. The claim for `sessionStorage` is narrower and worth stating exactly:
 * it is scoped to one tab's page session, survives a reload and a session
 * restore, is cleared when that tab closes, and is copied into a tab opened
 * FROM this one. Browsers may well write it to disk to make restore work — the
 * point is the scope and the lifetime, not an absence of persistence.
 *
 * That is enough for what this defends against, which is the id LEAVING the
 * device: an address bar, a history entry, a `Referer`, a shared link, a CSP
 * report. A handle that survives a restore in the same tab does not.
 *
 * It also keeps what a bare `/guest-profile` would have cost: distinct URLs
 * mean distinct history entries, so back and forward still walk the profiles
 * you actually visited rather than collapsing them into one.
 */

/** Bounded, so a long session cannot grow the map without limit. */
const MAX_HANDLES = 32;
const STORE_KEY = "opncue.profileHandles";

/**
 * The fallback when `sessionStorage` is unavailable — private modes, blocked
 * site data, and native, which has no such thing and needs none: there is no
 * address bar to keep an id out of, and no reload to survive.
 */
const memory = new Map<string, string>();

function session(): Storage | null {
  if (Platform.OS !== "web") return null;
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Accessing the property itself can throw where site data is blocked.
    return null;
  }
}

function readAll(store: Storage): Record<string, string> {
  try {
    const raw = store.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // Anything else in this key is not ours to trust or repair.
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function token(): string {
  const bytes = new Uint8Array(12);
  const c = globalThis.crypto;
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Mint a handle for `userId` and remember it for this tab. */
export function handleFor(userId: string): string {
  const key = token();
  memory.set(key, userId);
  if (memory.size > MAX_HANDLES) {
    // Insertion-ordered, so the oldest is the first key.
    const oldest = memory.keys().next().value;
    if (oldest !== undefined) memory.delete(oldest);
  }

  const store = session();
  if (store) {
    try {
      const all = readAll(store);
      all[key] = userId;
      const keys = Object.keys(all);
      for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_HANDLES))) {
        delete all[stale];
      }
      store.setItem(STORE_KEY, JSON.stringify(all));
    } catch {
      // Quota or a blocked write: the in-memory map still answers for this
      // navigation, and only a RELOAD loses it — which the screen handles.
    }
  }
  return key;
}

/** The user id a handle stands for, or null if this tab never minted it. */
export function userIdForHandle(handle: string | undefined): string | null {
  if (!handle) return null;
  const held = memory.get(handle);
  if (held) return held;
  const store = session();
  if (!store) return null;
  return readAll(store)[handle] ?? null;
}
